import * as React from 'react';

import * as cn from 'classnames';

import { Alert, Button, ButtonContainer, Modal, ModalFooter, ModalHeader, Spacer, Typography } from '@emburse/ui-core';

import { getAddressSuggestionData } from '@Shared/helpers/usps';
import { AddressSuggestionForUser, UserInputAddress } from '@Shared/types/user_address';

import * as style from './USPSAddressVerificationModal.styl';

interface USPSAddressVerificationModalProps {
  currentUserInputAddress: UserInputAddress;
  uspsAddressDiff: Record<string, string>;
  uspsReturnText: string;
  isOpen: boolean;
  onClose: () => void;
  onAcceptChanges: (suggestionData: AddressSuggestionForUser) => void;
}

const USPSAddressVerificationModal: React.FC<USPSAddressVerificationModalProps> = ({
  currentUserInputAddress,
  uspsAddressDiff,
  uspsReturnText,
  isOpen,
  onClose,
  onAcceptChanges
}) => {
  const suggestionData = getAddressSuggestionData(currentUserInputAddress, uspsAddressDiff);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const submit = async (changesToAccept: AddressSuggestionForUser) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onAcceptChanges(changesToAccept);
    } catch (e) {
      // do nothing
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalHeader = <ModalHeader title="Address Verification" showClose onCloseClicked={onClose} />;
  const modalFooter = (
    <ModalFooter>
      <ButtonContainer alignButtons="right">
        <Button variant="outlined" onClick={onClose} className="usps-accept-cancel">
          Cancel
        </Button>
        <Button color="primary" onClick={() => submit(suggestionData)} disabled={isSubmitting} className="usps-accept-changes">
          Accept Changes
        </Button>
      </ButtonContainer>
    </ModalFooter>
  );

  return (
    <Modal open={isOpen} onClose={onClose} modalHeader={modalHeader} modalFooter={modalFooter} className={style.global}>
      {uspsReturnText && (
        <Alert severity="warning" className="alert">
          The address you entered looks incomplete (missing apartment number, unit). You can use the suggested address below or click Cancel
          to revise it.
        </Alert>
      )}
      {!uspsReturnText && (
        <Typography>
          We found a match to your address. To continue, please select the below suggested address, or click Cancel to review your input.
        </Typography>
      )}
      <Spacer size={20} />
      <Typography variant="h6" gutterBottom>
        Suggested Address
      </Typography>
      <Typography className={cn({ 'changed-address': suggestionData.street_address.changed })}>
        {suggestionData.street_address.value}
      </Typography>
      <Typography>
        <span className={cn({ 'changed-address': suggestionData.address_city.changed })}>{suggestionData.address_city.value}</span>
        ,&nbsp;
        <span className={cn({ 'changed-address': suggestionData.address_state.changed })}>{suggestionData.address_state.value}</span>
        ,&nbsp;
        <span className={cn({ 'changed-address': suggestionData.postal_code.changed })}>{suggestionData.postal_code.value}</span>
      </Typography>
    </Modal>
  );
};

export { USPSAddressVerificationModal };
